<template>
<tableToolbar
  :features="{ search: true , exportExcel: false }"
  v-model:search="localSearch"
  :totalRecords="totalFiltered"
  @search="handleSearch">
  <template #right-actions>
    <div class="btn-group"  v-if="hasBulkActions && selectedKeys.size > 1">
        <button v-on:click="clearSelection"
          class="btn btn-sm btn-danger">          
            {{ selectedKeys.size }} selected
          </button>
        <div class="btn-group">
          <button 
            class="btn btn-sm btn-default"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false">
            <i class="fas fa-bars"></i>
          </button>
          <div  class="dropdown-menu dropdown-menu-right">
            <template   v-for="(action, i) in permittedBulkActions"  :key="i">
              <a
                class="dropdown-item"
                :class="[
                  action.class ?? '',
                  {
                    disabled:
                      action.disabled?.(selectedRows)
                  }
                ]"
                style="cursor:pointer"
                @click="
                  !action.disabled?.(selectedRows) &&
                  action.onClick(selectedRows)
                "
              >
                <i
                  v-if="action.icon"
                  :class="action.icon"
                  class="mr-2"
                />

                {{ action.label }}

              </a>
            </template>
          </div>
        </div>
    </div>
  </template>
</tableToolbar>

  <div class="table-responsive" style="min-height: 550px;">
    <table class="table table-hover table-striped smart">
      <thead>
        <tr class="bg-secondary">
          <!-- Checkbox "seleccionar todos" -->
          <th v-if="hasBulkActions" class="col-select">
            <input
              type="checkbox"
              class="smart-checkbox"
              :checked="isAllSelected"
              :indeterminate.prop="isIndeterminate"
              :disabled="!statusData || data.length === 0"
              @change="toggleSelectAll"
            />
          </th>

          <th class="hide-md-only">ITEM</th>

          <th v-for="col in visibleColumns" :key="col.key"
            :class="typeof col.class === 'string' ? col.class : undefined"
            >
            <span
              v-if="col.sortable"
              @click="col.onHeaderClick?.()"
              style="cursor:pointer"
              >
            {{ col.label }}
              <i class="fa fa-sort"    />
            </span>
            <span v-else>{{ col.label }}</span>

          </th>

          <th v-if="permittedActions.length">Actions</th>
        </tr>
      </thead>

      <!-- DATA -->
      <tbody v-if="statusData">
        <tr v-for="(row, index) in data" :key="rowKey ? rowKey(row, index) : index"
          :class="{ 'row-selected': isRowSelected(row, index) }"
        >
          <!-- Checkbox por fila -->
          <td v-if="hasBulkActions" class="col-select">
            <input
              type="checkbox"
              class="smart-checkbox"
              :checked="isRowSelected(row, index)"
              @change="toggleRow(row, index)"
            />
          </td>
          <td data-label="ITEM" class="hide-md-only">
            {{ getGlobalIndex(index) }}
          </td>

          <td v-for="col in visibleColumns" :key="col.key" :data-label="col.label"
            :class="typeof col.class === 'function' ? col.class(row) : col.class">
            <span v-if="!col.render">{{ row[col.key] }}</span>
            <!-- Con render: evaluar UNA sola vez -->
            <template v-else>
              <template v-for="(rendered, _) in [resolveRender(col, row)]" :key="_">
                <span v-if="isRenderResult(rendered) && rendered.type === 'badge'"
                  :class="[`${rendered.variant}`, rendered.class ?? '']"
                  :style="col.onClick ? 'cursor:pointer' : ''"
                  @click="col.onClick?.(row)">
                  {{ rendered.text }}
                  <i v-if="rendered.icon"
                    :class="rendered.icon"
                    v-tooltip="rendered.iconTooltip || undefined"
                    aria-hidden="true"/>
                </span>

                <span
                  v-else-if="isRenderResult(rendered) && rendered.type === 'text'"
                  :class="[rendered.bold ? 'font-weight-bold' : '', rendered.class ?? '']"
                  :style="col.onClick ? 'cursor:pointer' : ''"
                  @click="col.onClick?.(row)"
                >
                  {{ rendered.text }}
                </span>

                <span
                  v-else-if="isRenderResult(rendered) && rendered.type === 'html'"
                  :style="col.onClick ? 'cursor:pointer' : ''"
                  @click="col.onClick?.(row)"
                  v-html="rendered.html"
                />

                <span
                  v-else
                  :style="col.onClick ? 'cursor:pointer' : ''"
                  @click="col.onClick?.(row)">
                  {{ rendered }}
                </span>
              </template>
            </template>
          </td>

          <td v-if="permittedActions.length" class="text-right">
            <div class="btn-group">
              <template v-for="(action, i) in getVisibleActions(row)" :key="i">
                <button v-if="action.type !== 'dropdown'"
                  class="btn btn-sm btn-default"                  
                  :class="resolveClass(action.class, row)"
                  @click="handleActionClick(action, row)"
                  v-tooltip="getTooltip(action, row)">
                  <i :class="getIcon(action, row)" />
                </button>

                <div v-else class="btn-group">
                  <button
                    class="btn btn-sm btn-default dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    :class="resolveClass(action.class, row)"
                    v-tooltip="getTooltip(action, row)"
                  />
                  <div class="dropdown-menu dropdown-menu-right">
                    <a
                      v-for="(item, j) in action.items"
                      :key="j"
                      style="cursor: pointer"
                      class="dropdown-item"
                      :class="resolveClass(item.class, row)"
                      @click="item.onClick(row)">
                        <i v-if="item.icon" :class="item.icon" class="mr-2" />
                      {{ item.label }}
                    </a>
                  </div>
                </div>
              </template>
            </div>
          </td>
        </tr>

        <tr v-if="data.length === 0">
          <td :colspan="colspanValue">There is no data</td>
        </tr>
      </tbody>

      <!-- SKELETON -->
      <tbody v-else>
        <tr v-for="n in 10" :key="n" class="skeleton-row">
          <td v-if="hasBulkActions" class="col-select">
            <div class="sk sk-short" />
          </td>
          <td class="hide-md-only">
            <div class="sk sk-short" />
          </td>
          <td
            v-for="col in visibleColumns"
            :key="col.key"
            :class="typeof col.class === 'string' ? col.class : undefined"
          >
            <div :class="['sk', skeletonWidth(col)]" />
          </td>
          <td v-if="permittedActions.length">
            <div class="sk sk-actions" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>

</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { TableConfig, Column, RenderResult, DropdownItem, BulkAction,Features} from './TypeTable'

const props = defineProps<{
  statusData: boolean
  data: any[]
  config: TableConfig<any>
  totalFiltered:number
  startIndex?: number
  rowKey?: (row: any, index: number) => string | number
  search?: string  
  features?: Features
}>()

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'search', value: string): void
  (e: 'selectionChange', selected: any[]): void
}>()

const handleSearch = (value: string) => {
  emit('search', value)
}
const localSearch = computed({
  get: () => props.search ?? '',
  set: (value: string) => emit('update:search', value)
})


/** Set de claves de filas seleccionadas */
const selectedKeys = ref<Set<string | number>>(new Set())

const getRowKey = (row: any, index: number): string | number => {
  if (props.rowKey) return props.rowKey(row, index)
  if (props.config.rowId) return row[props.config.rowId as string]
  return index
}

const isRowSelected = (row: any, index: number) =>
  selectedKeys.value.has(getRowKey(row, index))

const toggleRow = (row: any, index: number) => {
  const key = getRowKey(row, index)
  const next = new Set(selectedKeys.value)
  next.has(key) ? next.delete(key) : next.add(key)
  selectedKeys.value = next
}

const isAllSelected = computed(
  () => props.data.length > 0 && selectedKeys.value.size === props.data.length
)

const isIndeterminate = computed(
  () => selectedKeys.value.size > 0 && selectedKeys.value.size < props.data.length
)

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedKeys.value = new Set()
  } else {
    selectedKeys.value = new Set(props.data.map((row, i) => getRowKey(row, i)))
  }
}

const clearSelection = () => {
  selectedKeys.value = new Set()
}

/** Array de objetos fila seleccionados (para pasar a las acciones masivas) */
const selectedRows = computed(() =>
  props.data.filter((row, i) => selectedKeys.value.has(getRowKey(row, i)))
)

/** Limpiar selección si cambia el dataset */
watch(
  () => props.data,
  () => clearSelection()
)

/** Emitir hacia el padre cada vez que cambia la selección */
watch(selectedRows, val => emit('selectionChange', val))

// ─── Bulk actions ──────────────────────────────────────────────────────────────


const hasBulkActions = computed(
  () => (props.config.bulkActions ?? []).length > 0
)

const permittedBulkActions = computed<BulkAction<any>[]>(() =>
  (props.config.bulkActions ?? []).filter(a => (a.permission ? a.permission() : true))
)

// ─── Columnas y acciones normales (sin cambios) ────────────────────────────────

const visibleColumns = computed(() =>
  (props.config.columns ?? []).filter(col => (col.permission ? col.permission() : true))
)

const permittedActions = computed(() =>
  (props.config.actions ?? [])
    .filter((action): action is NonNullable<typeof action> => {
      if (!action || typeof action !== 'object') return false
      return action.permission ? action.permission() : true
    })
    .map(action => {
      if (action.type !== 'dropdown') return action
      return {
        ...action,
        items: (action.items ?? []).filter((item: DropdownItem<any>) =>
          item.permission ? item.permission() : true
        ),
      }
    })
)

const getVisibleActions = (row: any) =>
  permittedActions.value
    .map(action => {
      if (action.type === 'dropdown') {
        const visibleItems = (action.items ?? []).filter((item: DropdownItem<any>) =>
          item.show ? item.show(row) : true
        )
        return visibleItems.length === 0 ? null : { ...action, items: visibleItems }
      }
      return action.show ? (action.show(row) ? action : null) : action
    })
    .filter((a): a is NonNullable<typeof a> => a !== null)


const handleActionClick = (action: any, row: any) => {
  if (!action || action.type === 'dropdown') return
  if (typeof action.onClick === 'function') action.onClick(row)
  else console.warn('Acción inválida detectada:', action)
}

const getIcon = (action: any, row: any): string =>
  typeof action.icon === 'function' ? action.icon(row) : action.icon ?? ''

const getTooltip = (action: any, row: any): { text: string } | undefined => {
  if (!action.tooltip) return undefined
  const value = typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip
  return value ? { text: value } : undefined
}

const colspanValue = computed(() => {
  let cols = visibleColumns.value.length + 1 // +1 ITEM
  if (permittedActions.value.length) cols++
  if (hasBulkActions.value) cols++           // +1 checkbox col
  return cols
})

const resolveClass = (
  cls: string | ((row: any) => string) | undefined,
  row: any
): string | undefined => {
  if (!cls) return undefined
  return typeof cls === 'function' ? cls(row) : cls
}

const getGlobalIndex = (index: number) => (props.startIndex ?? 0) - index

const isRenderResult = (val: RenderResult | string | number | null): val is RenderResult =>
  val !== null && typeof val === 'object' &&
  (val.type === 'badge' || val.type === 'text' || val.type === 'html')

const resolveRender = (col: Column<any>, row: any): RenderResult | string | number | null =>
  col.render ? col.render(row) : null

const skeletonWidth = (col: Column<any>): string => {
  if (col.skeletonSize) return `sk-${col.skeletonSize}`
  const len = col.label.length
  if (len <= 3) return 'sk-short'
  if (len <= 6) return 'sk-narrow'
  if (len <= 12) return 'sk-medium'
  return 'sk-wide'
}
</script>

<style scoped>
/* ── Checkbox column ──────────────────────────────────────────── */
.col-select {
  width: 40px;
  text-align: center;
  vertical-align: middle;
}

.smart-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #067aee;
}

/* ── Selected row highlight ──────────────────────────────────── */
.row-selected {
  background-color: #dce3f0 !important;
  box-shadow: inset 3px 0 0 #2C3E50;
}

/* ── Bulk action bar ─────────────────────────────────────────── */
.bulk-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  margin-bottom: 0.5rem;
  background: #2C3E50;
  color: #fff;
  font-size: 0.875rem;
}




/* ── Tabla base ─────────────────────────────────────────────── */
.table.smart {
  font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  color: #333;
}

.table.smart tbody td {
  font-weight: 400;
}

.table-hover tbody tr:hover {
  background-color: #97a5d46c;
  box-shadow: inset 2px 0 0 #2C3E50;
}

/* ── Skeleton ───────────────────────────────────────────────── */
@keyframes sk-shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}

.sk {
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 400px 100%;
  animation: sk-shimmer 1.4s ease infinite;
}

.sk-short   { width: 32px;  }
.sk-narrow  { width: 56px;  }
.sk-medium  { width: 96px;  }
.sk-wide    { width: 140px; }
.sk-actions { width: 72px;  }

.skeleton-row td {
  overflow: hidden;
  padding-top: 14px;
  padding-bottom: 14px;
  vertical-align: middle;
}

.skeleton-row:hover {
  background-color: transparent !important;
  box-shadow: none !important;
}
</style>